<template>
	<div>
		<h2 class="heading-5">Overview</h2>
		<div :class="['asset-details-overview-section', { 'no-image': !imageUrl }]">
			<div>
				<div class="asset-details-overview-section__overview-table module">
					<div v-if="sortedAssetClasses.length" class="display-flex justify-content-space-between p-100">
						<div class="text-color-content-secondary">Asset class</div>
						<div class="display-flex flex-direction-column gap-25">
							<div v-for="(assetClass, i) in sortedAssetClasses" :key="i">
								{{ assetClass }}
							</div>
						</div>
					</div>
					<div v-if="assetType" class="display-flex justify-content-space-between p-100">
						<div class="text-color-content-secondary">Asset type</div>
						<div>{{ assetType }}</div>
					</div>
					<div v-if="strategy" class="display-flex justify-content-space-between p-100">
						<div class="text-color-content-secondary">Strategy</div>
						<div>{{ strategy }}</div>
					</div>
					<div v-if="sortedFunds.length" class="display-flex justify-content-space-between p-100">
						<div class="text-color-content-secondary">Funds</div>
						<AssetDetailsFundTags :funds="sortedFunds" class="flex-direction-column align-items-flex-end" />
					</div>
				</div>
			</div>
			<div
				v-if="imageUrl"
				class="asset-details-overview-section__img-container"
				data-test="asset-details-overview-image"
			>
				<img :src="optimizedImageUrl" alt="" role="presentation" class="rounded" fetchpriority="high" />
			</div>
		</div>
	</div>
</template>
<script setup lang="ts">
import { Asset, AssetFundAllocation } from 'types/assets';
import AssetDetailsFundTags from '@components/marketing-site/asset-details/asset-details-fund-tags.vue';
import { computed } from 'vue';
import { sortAlphabetically } from '@utils/sorting';
import { useSanityImage } from '@utils/composables/use-sanity-image';

interface Props {
	asset: Asset;
}

const props = defineProps<Props>();

const assetType = props.asset.assetType.name ?? '';

const strategy = props.asset.realEstateAndPrivateCreditFields?.strategy.name ?? '';

const funds = props.asset.fundAllocations ?? [];

const imageUrl = props.asset.thumbnail?.url ?? '';

const optimizedImageUrl = computed(() => useSanityImage(imageUrl, 'hero'));

const sortedAssetClasses = computed((): Array<string> => {
	const assetClasses: Array<string> = Array.from(new Set(funds.map((fund) => fund.fund.assetClass.label ?? '')));
	return assetClasses.sort(sortAlphabetically);
});

const sortedFunds = computed((): Array<AssetFundAllocation> => {
	const temp = funds.slice();
	return temp.sort((fundA, fundB) => sortAlphabetically(fundA.fund.name, fundB.fund.name));
});
</script>

<style lang="scss" scoped>
@use '../../../styles/constants/_colors.scss' as *;
@use '../../../styles/utilities/respond-to.scss' as *;

.asset-details-overview-section {
	margin-top: 1rem;
	display: grid;
	align-items: start;

	@include respond-to(smo) {
		grid-template-rows: auto 1fr;
		gap: 24px;
	}

	@include respond-to(md) {
		grid-template-columns: 1fr 1fr;
		gap: 48px;

		&.no-image {
			width: 100%;
			grid-template-columns: unset;
			max-width: 75%;
		}
	}

	&__overview-table {
		& > *:not(:last-of-type) {
			border-bottom: 1px solid $base-border-color;
		}
	}

	&__img-container {
		img {
			object-fit: contain;
			height: 100%;
		}
		width: 100%;

		@include respond-to(md) {
			max-height: 100%;
		}
	}
}
</style>
